import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FieldGroup, FormDialog, notify, PasswordField } from '@/shared/ui';
import { useChangePassword } from '../api/changePassword';
import { type ChangePasswordValues, createChangePasswordSchema } from '../model/schema';

export interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY: ChangePasswordValues = {
  current_password: '',
  new_password: '',
  new_password_repeat: '',
};

/** Модалка «Сменить пароль» (ТЗ ACC 4.7.4; прототип — модалка `pwd`, 560 px). */
export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { t } = useTranslation('auth');
  const schema = useMemo(() => createChangePasswordSchema(t), [t]);
  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });
  const mutation = useChangePassword();

  useEffect(() => {
    if (open) form.reset(EMPTY);
  }, [open, form]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      onSubmit={async (values) => {
        await mutation.mutateAsync({
          current_password: values.current_password,
          new_password: values.new_password,
        });
        notify(t('changePassword.success'));
      }}
      width={560}
      overline={t('changePassword.overline')}
      title={t('changePassword.title')}
      lead={t('changePassword.lead')}
      footNote={t('changePassword.footNote')}
      submitLabel={t('changePassword.submit')}
    >
      <FieldGroup>
        <PasswordField<ChangePasswordValues>
          name="current_password"
          label={t('changePassword.current')}
          autoComplete="current-password"
          required
          wide
        />
        <PasswordField<ChangePasswordValues>
          name="new_password"
          label={t('changePassword.next')}
          placeholder={t('changePassword.nextPlaceholder')}
          autoComplete="new-password"
          required
        />
        <PasswordField<ChangePasswordValues>
          name="new_password_repeat"
          label={t('changePassword.repeat')}
          autoComplete="new-password"
          required
        />
      </FieldGroup>
    </FormDialog>
  );
}
